import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import API, { fmt } from '@/lib/shopApi';
import { Trash, ArrowLeft, CheckCircle, Plus, Minus, Truck, Storefront, Calendar, MapPin } from '@phosphor-icons/react';

export default function ShopMandjePage() {
  const navigate = useNavigate();
  const { me } = useOutletContext() || {};
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem('shop_cart') || '[]'));
  const [opmerking, setOpmerking] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  // Levering vs Afhaal — persisteren in localStorage
  const [leveringType, setLeveringType] = useState(() => localStorage.getItem('shop_levering_type') || 'afhaal');
  const [adres, setAdres] = useState(() => {
    try { return JSON.parse(localStorage.getItem('shop_levering_adres') || '{}'); } catch { return {}; }
  });
  const [gewensteDatum, setGewensteDatum] = useState(() => localStorage.getItem('shop_gewenste_datum') || '');
  const [adrErrors, setAdrErrors] = useState({});

  // Prefill adres uit klantgegevens bij eerste bezoek
  useEffect(() => {
    if (me?.klant && Object.keys(adres).length === 0) {
      setAdres({
        tav: me.klant.tav || '',
        straat: me.klant.straat || '',
        nr: me.klant.nr || '',
        bus: me.klant.bus || '',
        postcode: me.klant.postcode || '',
        stad: me.klant.stad || '',
        land: me.klant.land || 'BE',
        opmerking: '',
      });
      // Indien klant een specifiek leveradres heeft, default direct naar 'levering'
      if (me.klant.heeft_apart_leveradres) {
        setLeveringType('levering');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    localStorage.setItem('shop_cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('shop_cart_change'));
  }, [cart]);
  useEffect(() => { localStorage.setItem('shop_levering_type', leveringType); }, [leveringType]);
  useEffect(() => { localStorage.setItem('shop_levering_adres', JSON.stringify(adres)); }, [adres]);
  useEffect(() => { localStorage.setItem('shop_gewenste_datum', gewensteDatum); }, [gewensteDatum]);

  const totaal = useMemo(() => cart.reduce((s, x) => s + Number(x.prijs || 0) * Number(x.aantal || 0), 0), [cart]);
  const vz = me?.verzendkosten;
  const verzendKost = useMemo(() => (vz && leveringType === 'levering' && totaal > 0 && totaal < Number(vz.drempel)) ? Number(vz.kost) : 0, [vz, totaal, leveringType]);

  const updQty = (id, delta) => setCart((c) => c.map((x) => x.art_id === id ? { ...x, aantal: Math.max(0, Number(x.aantal) + delta) } : x).filter((x) => x.aantal > 0));
  const rm = (id) => setCart((c) => c.filter((x) => x.art_id !== id));

  // Validatie: Belgische postcode = 4 cijfers; basisvalidatie van verplichte velden
  const validateAdres = () => {
    const e = {};
    if (leveringType === 'levering') {
      if (!(adres.straat || '').trim()) e.straat = 'Straat is verplicht';
      if (!(adres.nr || '').trim()) e.nr = 'Huisnr verplicht';
      if (!(adres.postcode || '').trim()) e.postcode = 'Postcode verplicht';
      else if ((adres.land || 'BE') === 'BE' && !/^\d{4}$/.test(adres.postcode.trim())) e.postcode = 'Postcode moet 4 cijfers zijn';
      if (!(adres.stad || '').trim()) e.stad = 'Stad/gemeente verplicht';
    }
    setAdrErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (cart.length === 0) return;
    if (!validateAdres()) {
      alert('Vul aub de verplichte adresvelden in');
      return;
    }
    setBusy(true);
    try {
      const r = await API.post('/shop/order', {
        items: cart.map((x) => ({ art_id: x.art_id, aantal: x.aantal })),
        opmerking,
        levering_type: leveringType,
        levering_adres: leveringType === 'levering' ? adres : null,
        gewenste_datum: gewensteDatum || null,
      });
      setDone(r.data);
      setCart([]);
    } catch (e) {
      alert('Bestelling mislukt: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const afhaalAdres = me?.leverancier?.afhaal_adres || {};
  const afhaalUren = me?.leverancier?.afhaal_uren || '';

  if (done) {
    return (
      <div className="bg-white border border-green-300 p-8 text-center max-w-lg mx-auto" data-testid="shop-order-success">
        <CheckCircle size={48} weight="bold" className="text-green-600 mx-auto mb-3" />
        <div className="text-2xl font-black text-green-700 mb-2">Bestelling ontvangen!</div>
        <div className="text-sm text-gray-600 mb-4">
          Bestelnummer: <strong className="shop-text-primary">{done.id}</strong><br />
          Totaal: <strong>{fmt(done.totaal)}</strong>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-[#002FA7] text-xs font-bold mb-4">
          {done.levering_type === 'levering' ? <><Truck size={14} weight="bold" /> Levering aangevraagd</> : <><Storefront size={14} weight="bold" /> Klaar voor afhaal</>}
        </div>
        <p className="text-xs text-gray-500 mb-6">
          Uw leverancier ontvangt automatisch een melding en zal de bestelling binnen 24 uur bevestigen.
        </p>
        <div className="flex gap-2 justify-center">
          <button onClick={() => navigate('/shop/bestellen')} className="px-4 py-2 shop-bg-primary text-white font-bold text-sm">Nieuwe bestelling</button>
          <button onClick={() => navigate('/shop/bestellingen')} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-sm">Mijn bestellingen</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="shop-mandje-page">
      <button onClick={() => navigate('/shop/bestellen')} className="text-sm text-gray-500 hover:shop-text-primary mb-3 inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Verder winkelen
      </button>
      <h1 className="text-2xl font-black mb-4">Uw mandje</h1>
      {cart.length === 0 ? (
        <div className="text-center text-gray-400 py-12">Uw mandje is leeg.</div>
      ) : (
        <>
          {/* Lijnen */}
          <div className="bg-white border border-gray-200 mb-4">
            {cart.map((x) => (
              <div key={x.art_id} className="px-3 py-3 border-b border-gray-100 flex items-center gap-3" data-testid={`mandje-line-${x.art_code}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-gray-500 font-mono">{x.art_code}</div>
                  <div className="text-sm font-semibold truncate">{x.omschrijving}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => updQty(x.art_id, -1)} className="bg-gray-100 hover:bg-gray-200 w-8 h-8 flex items-center justify-center" data-testid={`mandje-min-${x.art_code}`}><Minus size={12} /></button>
                  <span className="font-bold w-10 text-center">{x.aantal}</span>
                  <button onClick={() => updQty(x.art_id, 1)} className="shop-bg-primary text-white w-8 h-8 flex items-center justify-center" data-testid={`mandje-plus-${x.art_code}`}><Plus size={12} weight="bold" /></button>
                </div>
                <div className="w-24 text-right font-bold shop-text-primary">{fmt(x.prijs * x.aantal)}</div>
                <button onClick={() => rm(x.art_id)} className="text-gray-400 hover:text-red-500" title="Verwijderen"><Trash size={14} /></button>
              </div>
            ))}
            <div className="px-3 py-3 bg-gray-50">
              {verzendKost > 0 && (
                <div className="flex justify-between items-center text-sm mb-1" data-testid="mandje-verzendkosten">
                  <span className="text-gray-600 inline-flex items-center gap-1"><Truck size={13} /> Verzendkosten <span className="text-[10px] text-gray-400">(gratis vanaf {fmt(vz.drempel)})</span></span>
                  <span className="font-bold">{fmt(verzendKost)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold">Totaal (excl. BTW)</span>
                <span className="text-xl font-black shop-text-primary">{fmt(totaal + verzendKost)}</span>
              </div>
            </div>
          </div>

          {/* ─── Levering vs Afhaal ─────────────────────────── */}
          <div className="bg-white border border-gray-200 mb-4 p-4" data-testid="shop-levering-keuze">
            <div className="text-xs uppercase font-bold text-gray-500 mb-2">Hoe wilt u uw bestelling ontvangen?</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              <button
                type="button"
                onClick={() => setLeveringType('afhaal')}
                className={`p-3 text-left transition-all ${leveringType === 'afhaal' ? 'shop-border-primary border-2 bg-blue-50' : 'border border-gray-200 hover:border-gray-400'}`}
                data-testid="lev-afhaal"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Storefront size={20} weight="bold" className={leveringType === 'afhaal' ? 'shop-text-primary' : 'text-gray-400'} />
                  <span className="font-bold">Afhaal</span>
                  {leveringType === 'afhaal' && <span className="ml-auto text-[10px] shop-text-primary font-black">✓ GEKOZEN</span>}
                </div>
                <div className="text-[11px] text-gray-500">Haal zelf af op locatie van uw leverancier</div>
              </button>
              <button
                type="button"
                onClick={() => setLeveringType('levering')}
                className={`p-3 text-left transition-all ${leveringType === 'levering' ? 'shop-border-primary border-2 bg-blue-50' : 'border border-gray-200 hover:border-gray-400'}`}
                data-testid="lev-levering"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Truck size={20} weight="bold" className={leveringType === 'levering' ? 'shop-text-primary' : 'text-gray-400'} />
                  <span className="font-bold">Levering</span>
                  {leveringType === 'levering' && <span className="ml-auto text-[10px] shop-text-primary font-black">✓ GEKOZEN</span>}
                </div>
                <div className="text-[11px] text-gray-500">Wij komen leveren op uw adres</div>
              </button>
            </div>

            {/* Afhaal-info */}
            {leveringType === 'afhaal' && (afhaalAdres.straat || afhaalUren) && (
              <div className="bg-blue-50 border border-blue-100 p-3 text-xs flex items-start gap-2" data-testid="afhaal-info">
                <MapPin size={14} className="shop-text-primary mt-0.5 shrink-0" />
                <div>
                  {afhaalAdres.straat && (
                    <div className="font-semibold text-gray-700">
                      {afhaalAdres.straat} {afhaalAdres.nr}{afhaalAdres.postcode && (`, ${afhaalAdres.postcode} ${afhaalAdres.stad}`)}
                    </div>
                  )}
                  {afhaalUren && <div className="text-gray-500">Openingsuren: {afhaalUren}</div>}
                </div>
              </div>
            )}

            {/* Leveradres formulier */}
            {leveringType === 'levering' && (
              <div className="space-y-2" data-testid="lev-adres-form">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Leveradres</div>
                  {me?.klant?.heeft_apart_leveradres && (
                    <span className="text-[10px] text-green-600 font-bold">✓ Geregistreerd leveradres</span>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="T.a.v. (optioneel — contactpersoon op locatie)"
                  value={adres.tav || ''}
                  onChange={(e) => setAdres({ ...adres, tav: e.target.value })}
                  className="w-full border-2 border-gray-200 px-3 py-2 text-sm"
                  data-testid="adres-tav"
                />
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-2">
                    <input
                      type="text"
                      placeholder="Straat *"
                      value={adres.straat || ''}
                      onChange={(e) => setAdres({ ...adres, straat: e.target.value })}
                      className={`w-full border-2 ${adrErrors.straat ? 'border-red-400' : 'border-gray-200'} px-3 py-2 text-sm`}
                      data-testid="adres-straat"
                    />
                    {adrErrors.straat && <div className="text-[10px] text-red-500 mt-0.5">{adrErrors.straat}</div>}
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Nr *"
                      value={adres.nr || ''}
                      onChange={(e) => setAdres({ ...adres, nr: e.target.value })}
                      className={`w-full border-2 ${adrErrors.nr ? 'border-red-400' : 'border-gray-200'} px-3 py-2 text-sm`}
                      data-testid="adres-nr"
                    />
                    {adrErrors.nr && <div className="text-[10px] text-red-500 mt-0.5">{adrErrors.nr}</div>}
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="Bus"
                      value={adres.bus || ''}
                      onChange={(e) => setAdres({ ...adres, bus: e.target.value })}
                      className="w-full border-2 border-gray-200 px-3 py-2 text-sm"
                      data-testid="adres-bus"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <input
                      type="text"
                      placeholder="Postcode *"
                      value={adres.postcode || ''}
                      onChange={(e) => setAdres({ ...adres, postcode: e.target.value })}
                      className={`w-full border-2 ${adrErrors.postcode ? 'border-red-400' : 'border-gray-200'} px-3 py-2 text-sm`}
                      data-testid="adres-postcode"
                    />
                    {adrErrors.postcode && <div className="text-[10px] text-red-500 mt-0.5">{adrErrors.postcode}</div>}
                  </div>
                  <div className="col-span-2">
                    <input
                      type="text"
                      placeholder="Stad / Gemeente *"
                      value={adres.stad || ''}
                      onChange={(e) => setAdres({ ...adres, stad: e.target.value })}
                      className={`w-full border-2 ${adrErrors.stad ? 'border-red-400' : 'border-gray-200'} px-3 py-2 text-sm`}
                      data-testid="adres-stad"
                    />
                    {adrErrors.stad && <div className="text-[10px] text-red-500 mt-0.5">{adrErrors.stad}</div>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <select
                    value={adres.land || 'BE'}
                    onChange={(e) => setAdres({ ...adres, land: e.target.value })}
                    className="w-full border-2 border-gray-200 px-3 py-2 text-sm"
                    data-testid="adres-land"
                  >
                    <option value="BE">België</option>
                    <option value="NL">Nederland</option>
                    <option value="FR">Frankrijk</option>
                    <option value="DE">Duitsland</option>
                    <option value="LU">Luxemburg</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Bv. 'Achterkant magazijn'"
                    value={adres.opmerking || ''}
                    onChange={(e) => setAdres({ ...adres, opmerking: e.target.value })}
                    className="col-span-2 w-full border-2 border-gray-200 px-3 py-2 text-sm"
                    data-testid="adres-opmerking"
                  />
                </div>
              </div>
            )}

            {/* Gewenste datum (beide types) */}
            <div className="mt-4 flex items-center gap-2">
              <Calendar size={16} className="shop-text-primary" />
              <label className="text-xs font-bold text-gray-600">Gewenste {leveringType === 'levering' ? 'leverdatum' : 'afhaaldatum'} (optioneel):</label>
              <input
                type="date"
                value={gewensteDatum}
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                onChange={(e) => setGewensteDatum(e.target.value)}
                className="border border-gray-200 px-2 py-1 text-sm"
                data-testid="gewenste-datum"
              />
            </div>
          </div>

          <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Opmerking (optioneel)</label>
          <textarea className="w-full border border-gray-200 p-2 text-sm mb-4 min-h-[80px]" placeholder="Bv. contactpersoon, bijzonderheden…" value={opmerking} onChange={(e) => setOpmerking(e.target.value)} data-testid="shop-opmerking" />
          <div className="flex justify-end gap-2">
            <button onClick={() => navigate('/shop/bestellen')} className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-sm font-bold">Verder winkelen</button>
            <button onClick={submit} disabled={busy} className="px-6 py-2.5 shop-bg-primary  text-white font-bold disabled:opacity-50" data-testid="shop-bevestig-btn">
              {busy ? 'Versturen…' : '✓ Bestelling indienen'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
